import { PathElement } from './path-element.ts';
import { MatchingContext } from '../path-pattern.ts';
import type { PathSegment } from '../../../../../types/path';

/*internal*/
export class RegexPathElement extends PathElement {

    readonly #regex: Uint16Array;
    readonly #caseSensitive: boolean;
    readonly #pattern: RegExp;
    readonly #wildcardCount;
    readonly #variableNames: readonly string[];

    constructor(pos: number, regex: Uint16Array, caseSensitive: boolean, completePattern: Uint16Array, separator: number) {
        super(pos, separator);
        this.#regex = regex;
        this.#caseSensitive = caseSensitive;
        [this.#pattern, this.#wildcardCount, this.#variableNames] = this.#buildPattern(regex, completePattern);
    }

    #buildPattern(regex: Uint16Array, _completePattern: Uint16Array): [RegExp, number, readonly string[]] {
        const text = String.fromCharCode(...regex);
        const variableNames: string[] = [];
        let wildcardCount = 0;
        const patternBuilder: string[] = [];

        let pos = 0;
        while (pos < text.length) {
            const ch = text.charAt(pos);

            if (ch === '{') {
                // Start of a variable capture
                const closeBrace = text.indexOf('}', pos);
                if (closeBrace === -1) {
                    throw new SyntaxError(`Missing closing brace in pattern: ${text}`);
                }

                const captureContent = text.substring(pos + 1, closeBrace);
                const colonIndex = captureContent.indexOf(':');

                let variableName: string;
                let constraint: string;

                if (colonIndex !== -1) {
                    // Has constraint: {varname:constraint}
                    variableName = captureContent.substring(0, colonIndex);
                    constraint = captureContent.substring(colonIndex + 1);
                } else {
                    // No constraint: {varname} - default to match anything except separator
                    variableName = captureContent;
                    constraint = '[^/]*';
                }

                // Handle {*varname} syntax for capture-all
                if (variableName.startsWith('*')) {
                    variableName = variableName.substring(1);
                    constraint = '.*';
                }

                variableNames.push(variableName);
                patternBuilder.push('(', constraint, ')');

                pos = closeBrace + 1;
            } else if (ch === '*') {
                // Wildcard - match anything except separator
                wildcardCount++;
                patternBuilder.push('[^/]*');
                pos++;
            } else if (ch === '?') {
                // Single character wildcard
                patternBuilder.push('.');
                pos++;
            } else if ('.+()[]^$|\\'.includes(ch)) {
                // Escape regex special characters
                patternBuilder.push('\\', ch);
                pos++;
            } else {
                patternBuilder.push(ch);
                pos++;
            }
        }

        const patternString = patternBuilder.join('');
        const flags = this.#caseSensitive ? 's' : 'si';

        return [new RegExp(`^${patternString}$`, flags), wildcardCount, variableNames];
    }

    matches(pathIndex: number, matchingContext: MatchingContext): boolean {
        const textToMatch = matchingContext.pathElementValue(pathIndex);
        const match = this.#pattern.exec(textToMatch);
        let matches = match !== null && match.index === 0 && match[0].length === textToMatch.length;
        if (matches) {
            if (this.noMorePattern()) {
                if (matchingContext.determinedRemainingPath && (this.#variableNames.length === 0 || textToMatch.length !== 0)) {
                    matchingContext.remainingPathIndex = pathIndex + 1;
                    matches = true;
                }
                else {
                    // no more pattern, is there more data?
                    // if pattern is capturing variables there must be some actual data to bind to them
                    matches = (pathIndex + 1 >= matchingContext.pathLength) &&
                        (this.#variableNames.length === 0 || textToMatch.length !== 0);
                }
            }
            else {
                matches = (this.next !== undefined && this.next.matches(pathIndex + 1, matchingContext));
            }
        }

        if (matches && matchingContext.extractingVariables) {
            // process captures
            if (this.#variableNames.length + 1 !== match?.length) {
                throw new Error(`The number of capturing groups in the pattern segment
                ${this.#pattern} does not match the number of variables it defines, which can occur if capturing groups are used in the regex itself. Use non-capturing groups (?:...) instead.`);
            }
            for (let i = 1; i < match.length; i++) {
                const name = this.#variableNames[i - 1];
                const value = match[i];
                matchingContext.set(
                    name,
                    value,
                    (i == this.#variableNames.length) ? (matchingContext.pathElements[pathIndex] as PathSegment).parameters : PathElement.NO_PARAMETERS)
            }
        }
        return matches;
    }

    get variableNames(): readonly string[] {
        return this.#variableNames;
    }

    get normalizedLength() {
        let varsLength = 0;
        for (const name of this.#variableNames) {
            varsLength += name.length;
        }
        return (this.#regex.length - varsLength - this.#variableNames.length);
    }

    get chars(): Uint16Array {
        return this.#regex;
    }

    get captureCount() {
        return this.#variableNames.length;
    }

    get wildcardCount() {
        return this.#wildcardCount;
    }

    get score() {
        return this.captureCount * PathElement.CAPTURE_VARIABLE_WEIGHT + this.wildcardCount * PathElement.WILDCARD_WEIGHT;
    }

    toString() {
        return `Regex(${String.fromCharCode(...(this.#regex))})`;
    }


}
